import React, { useState, useEffect, useRef, useCallback } from "react";
import { useParams } from "react-router-dom";
import { Check, X, Loader2, Music } from "lucide-react";
import api from "../Utils/api";

const isYouTubeUrl = (u) =>
  typeof u === "string" &&
  /^(https?:\/\/)?(www\.|music\.)?(youtube\.com|youtu\.be)\//i.test((u || "").trim());

const StatusRow = ({ submission }) => {
  const { status, url, title, error } = submission;
  let icon;
  let tint;
  if (status === "done") {
    icon = <Check size={16} />;
    tint = "text-emerald-400";
  } else if (status === "error") {
    icon = <X size={16} />;
    tint = "text-red-400";
  } else {
    icon = <Loader2 size={16} className="animate-spin" />;
    tint = "text-amber-400";
  }
  return (
    <div className="flex items-center gap-3 p-3 rounded-lg bg-neutral-900 border border-neutral-800">
      <span className={tint}>{icon}</span>
      <div className="flex-1 min-w-0">
        <p className="m-0 text-sm text-white truncate">{title || url}</p>
        {status === "error" && (
          <p className="m-0 text-xs text-red-400 truncate">{error || "Failed"}</p>
        )}
      </div>
      <span className="text-xs text-neutral-400 capitalize">
        {status === "done" ? "Added" : status}
      </span>
    </div>
  );
};

export default function SubmitPage() {
  const { token } = useParams();
  const [loading, setLoading] = useState(true);
  const [active, setActive] = useState(false);
  const [maxLinks, setMaxLinks] = useState(1);
  const [urls, setUrls] = useState([""]);
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState(null);
  const [mySubIds, setMySubIds] = useState([]);
  const [submissions, setSubmissions] = useState([]);
  const pollRef = useRef(null);

  // Validate the link on load.
  useEffect(() => {
    let cancelled = false;
    api
      .getDjSessionStatus(token)
      .then((res) => {
        if (cancelled) return;
        setActive(!!res.active);
        // The DJ decides how many links a guest may add.
        const n = Math.max(1, Math.min(20, res.maxLinks || 1));
        setMaxLinks(n);
        setUrls(Array(n).fill(""));
        // Let the DJ know the QR was scanned.
        if (res.active) api.markDjSessionScanned(token).catch(() => {});
      })
      .catch(() => !cancelled && setActive(false))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [token]);

  const setUrlAt = (i, value) =>
    setUrls((prev) => prev.map((u, idx) => (idx === i ? value : u)));

  const pollStatus = useCallback(() => {
    api
      .getDjSessionStatus(token)
      .then((res) => {
        setActive(!!res.active);
        setSubmissions(res.submissions || []);
      })
      .catch(() => {});
  }, [token]);

  const stopPolling = () => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
  };

  // Stop polling once everything we submitted has settled.
  useEffect(() => {
    if (!mySubIds.length) return;
    const mine = submissions.filter((s) => mySubIds.includes(s.id));
    const allSettled =
      mine.length === mySubIds.length &&
      mine.every((s) => s.status === "done" || s.status === "error");
    if (allSettled) stopPolling();
  }, [submissions, mySubIds]);

  useEffect(() => () => stopPolling(), []);

  const handleSubmit = async () => {
    setFormError(null);
    const cleaned = urls.map((u) => u.trim()).filter(Boolean);
    const valid = cleaned.filter(isYouTubeUrl);
    if (!valid.length) {
      setFormError("Please enter at least one valid YouTube link.");
      return;
    }
    if (valid.length < cleaned.length) {
      setFormError("Some links weren't YouTube links and were skipped.");
    }
    setSubmitting(true);
    try {
      const res = await api.submitDjLinks(token, valid);
      const created = res.submissions || [];
      setMySubIds((prev) => [...new Set([...prev, ...created.map((s) => s.id)])]);
      setSubmissions((prev) => {
        const map = new Map(prev.map((s) => [s.id, s]));
        created.forEach((s) => map.set(s.id, s));
        return Array.from(map.values());
      });
      // Reset the form for the next batch.
      setUrls(Array(maxLinks).fill(""));
      stopPolling();
      pollRef.current = setInterval(pollStatus, 2500);
    } catch (e) {
      setFormError(e?.message || "Submission failed. The link may have expired.");
      if (e?.status === 410) setActive(false);
    } finally {
      setSubmitting(false);
    }
  };

  const mine = submissions.filter((s) => mySubIds.includes(s.id));

  return (
    <div className="min-h-screen bg-neutral-950 text-white flex flex-col items-center px-4 py-10">
      <div className="w-full max-w-md space-y-6">
        <div className="flex items-center gap-2 justify-center">
          <Music className="text-accent" />
          <h1 className="text-xl font-semibold">Add to the DJ set</h1>
        </div>

        {loading ? (
          <div className="flex justify-center py-12">
            <Loader2 className="animate-spin text-accent" size={28} />
          </div>
        ) : !active ? (
          <div className="text-center py-12 space-y-2">
            <p className="text-lg">This link is no longer active.</p>
            <p className="text-sm text-neutral-400">
              Ask the DJ for a fresh QR code.
            </p>
          </div>
        ) : (
          <>
            <p className="text-sm text-neutral-400 text-center">
              Paste YouTube links and they'll be added to the live set.
            </p>

            <div className="space-y-3">
              {urls.map((u, i) => (
                <input
                  key={i}
                  type="url"
                  inputMode="url"
                  placeholder={`https://youtube.com/...  (#${i + 1})`}
                  value={u}
                  onChange={(e) => setUrlAt(i, e.target.value)}
                  className="w-full px-3 py-2 bg-neutral-900 border border-neutral-700 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-accent"
                />
              ))}

              {formError && <p className="text-sm text-amber-400">{formError}</p>}

              <button
                onClick={handleSubmit}
                disabled={submitting}
                className="w-full py-2.5 rounded-lg font-medium bg-accent text-white hover:opacity-90 transition-opacity disabled:opacity-50 inline-flex items-center justify-center gap-2"
              >
                {submitting && <Loader2 size={16} className="animate-spin" />}
                Add to set
              </button>
            </div>

            {mine.length > 0 && (
              <div className="space-y-2 pt-2">
                <p className="text-sm font-medium text-neutral-300">
                  Your submissions
                </p>
                {mine.map((s) => (
                  <StatusRow key={s.id} submission={s} />
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
